/**
 * AbsorptionSystem — pulls nearby food toward the cell, destroys on contact,
 * awards biomass/DNA, and fires absorption events.
 *
 * Contact with predators is handled here too (damage to player; thorns back).
 */

import type { Container } from 'pixi.js';
import type { World } from '@/core/ECS';
import { Absorbable, Hostile, PlayerCell, Position, Powerup, Renderable, Velocity } from '@/components';
import { distSq } from '@/utils/math';
import { bus } from '@/core/EventBus';
import { player } from './PlayerState';
import type { MutationSystem } from './MutationSystem';
import type { EnvironmentSystem } from './EnvironmentSystem';
import type { ComboSystem } from './ComboSystem';
import type { PredatorDef } from '@/data/types';
import { createPredator } from '@/entities/createPredator';

const SHIELD_DURATION = 5;
const BOMB_KILL_RATIO = 0.7;

export class AbsorptionSystem {
  // Same-type food streak — only counts while the absorption halo is OFF
  // (right-mouse held past long-press threshold), so a player who wants
  // the bonus must commit to manually steering the cell over individual
  // food items instead of leaning on passive pull.
  //
  // The bonus only kicks in once the chain reaches STREAK_MIN of the
  // same food type. Below that the player sees no UI/multiplier — it's a
  // "you have to commit" gate that rewards intentional play.
  private streakFoodId: string | null = null;
  private streakCount = 0;
  private streakActive = false;
  private readonly STREAK_MIN = 5;

  constructor(
    private mutations: MutationSystem,
    private environment: EnvironmentSystem,
    private combo: ComboSystem,
    private predatorDefs: Record<string, PredatorDef> = {},
    private spawnParent: Container | null = null,
  ) {}

  /** Streak multiplier: kicks in once the chain reaches STREAK_MIN, then
   *  ramps up faster than the bloom-clear combo. The curve plus a higher
   *  cap (4×) makes a long streak meaningfully reshape the run instead
   *  of being a sub-token bonus. */
  private streakMultiplier(): number {
    if (this.streakCount < this.STREAK_MIN) return 1;
    const over = this.streakCount - this.STREAK_MIN + 1;
    return Math.min(4, 1 + Math.log2(over + 1) * 0.8);
  }

  update(world: World, dt: number): void {
    // find the player
    let px = 0, py = 0, cellR = 0, fieldR = 0;
    let found = false;
    let haloOn = false;
    let playerCell: ReturnType<World['get']> = undefined;
    let playerEntity = 0;
    for (const e of world.query([PlayerCell, Position])) {
      const cp = world.get(e, Position)!;
      const pc = world.get(e, PlayerCell)!;
      px = cp.x; py = cp.y;
      cellR = pc.radius;
      haloOn = pc.haloEnabled;
      // Field only exists while the player holds the halo on. Without
      // hold, fieldR collapses to the cell's contact radius — food has
      // to be physically touched, which is what gates the streak combo.
      fieldR = haloOn ? pc.absorptionRadius : cellR;
      if (haloOn && pc.pulseRemaining > 0) fieldR *= 1 + pc.pulseRemaining;
      playerCell = pc;
      playerEntity = e;
      found = true;
    }
    if (!found) return;
    this.streakActive = !haloOn;

    const envEffects = this.environment.getEffects();
    const envAbsMult = envEffects.absorptionRadiusMult ?? 1;
    const effectiveField = fieldR * envAbsMult;
    const fieldSq = effectiveField * effectiveField;
    const absorbSq = (cellR + 2) * (cellR + 2);

    // iterate food
    const destroy: number[] = [];
    for (const e of world.query([Absorbable, Position, Velocity, Renderable])) {
      const fp = world.get(e, Position)!;
      const fv = world.get(e, Velocity)!;
      const fa = world.get(e, Absorbable)!;
      const dsq = distSq(px, py, fp.x, fp.y);

      if (dsq < absorbSq + fa.radius * fa.radius) {
        // absorbed
        this.awardFood(fa, fp.x, fp.y);
        destroy.push(e);
        continue;
      }

      if (dsq < fieldSq) {
        // steer toward player along curved path
        const dx = px - fp.x;
        const dy = py - fp.y;
        const d = Math.sqrt(dsq) || 1;
        const pull = 220 * (1 - d / effectiveField) + 120;
        // curved path: add perpendicular bias based on position parity
        const perp = (e % 2 === 0 ? 1 : -1) * 80 * (1 - d / effectiveField);
        fv.vx = (dx / d) * pull + (-dy / d) * perp * 0.25;
        fv.vy = (dy / d) * pull + (dx / d) * perp * 0.25;
        fp.x += fv.vx * dt;
        fp.y += fv.vy * dt;
      }
    }
    for (const id of destroy) {
      const r = world.get(id, Renderable);
      if (r) r.root.parent?.removeChild(r.root);
      world.destroyEntity(id);
    }

    // --- powerups ---
    // Pickups use a generous contact radius (cellR + powerup radius + slack)
    // so the player doesn't have to nail an exact hit on a rare item.
    const pickupDestroy: number[] = [];
    for (const e of world.query([Powerup, Position])) {
      const pp = world.get(e, Position)!;
      const pw = world.get(e, Powerup)!;
      const contactR = cellR + pw.radius + 8;
      if (distSq(px, py, pp.x, pp.y) < contactR * contactR) {
        if (pw.kind === 'bomb') this.detonateBomb(world, pp.x, pp.y);
        else this.activateShield(playerCell as any, pp.x, pp.y);
        pickupDestroy.push(e);
      }
    }
    for (const id of pickupDestroy) {
      const r = world.get(id, Renderable);
      if (r) r.root.parent?.removeChild(r.root);
      world.destroyEntity(id);
    }

    // --- predator contact ---
    const mutReduction = this.mutations.getEffects().damageReduction ?? 0;
    const metaReduction = player.stats.metaDamageReduction ?? 0;
    const reduction = Math.min(0.9, mutReduction + metaReduction);
    const damageTaken = 1 - reduction;
    const thorns = this.mutations.getEffects().thorns ?? 0;
    for (const e of world.query([Hostile, Position, Renderable])) {
      const hp = world.get(e, Position)!;
      const h = world.get(e, Hostile)!;
      const dsq = distSq(px, py, hp.x, hp.y);
      const contactR = cellR + h.radius * 0.9;
      if (dsq < contactR * contactR) {
        // Lytic phages bypass shield — invuln only blocks normal attacks.
        const shielded = (playerCell as any).invulnRemaining > 0;
        const canHit = h.attackTimer <= 0 && (!shielded || h.piercesShield);
        if (canHit) {
          const dmg = h.damage * damageTaken;
          player.damage(dmg, h.predatorId);
          bus.emit('fx:screenShake', { intensity: 10, duration: 0.3 });
          bus.emit('fx:flash', { color: h.piercesShield ? 0xff60a0 : 0xff4040, alpha: 0.18, duration: 0.2 });
          bus.emit('audio:cue', { id: 'damage' });
          h.attackTimer = h.attackCooldown;
          if (thorns > 0) {
            h.hp -= thorns;
          }
          // Getting hit drops the same-type streak — greedy chains carry risk.
          if (this.streakCount > 0) {
            this.streakFoodId = null;
            this.streakCount = 0;
            bus.emit('combo:streak', { foodId: null, count: 0, multiplier: 1 });
          }
        }
        if (h.hp <= 0) {
          // predator killed — treat as bacteria-like absorption with predator bonus
          const effects = this.mutations.getEffects();
          const predBonus = 1 + (effects.biomassFromPredator ?? 0);
          const amount = 20 * predBonus * player.stats.biomassMult * (this.combo.multiplier);
          player.addBiomass(amount);
          bus.emit('fx:floatingText', { x: hp.x, y: hp.y, text: `+${Math.round(amount)}`, color: 0xff9f9f });
          bus.emit('fx:particles', { x: hp.x, y: hp.y, count: 10, color: h.codexId ? 0xfc8181 : 0xfc8181 });
          bus.emit('fx:screenShake', { intensity: 4, duration: 0.2 });
          bus.emit('audio:pop', { pitch: 0.9 });
          if (h.codexId) bus.emit('codex:unlocked', { entryId: h.codexId });
          bus.emit('predator:killed', { predatorId: h.predatorId, x: hp.x, y: hp.y });
          // Apex variants split into smaller copies on death — kill them
          // once and you get a fresh wave to deal with.
          if (h.splitsInto && this.spawnParent) {
            const split = h.splitsInto;
            const childDef = this.predatorDefs[split.predatorId];
            if (childDef) {
              const radMul = split.radiusMult ?? 0.8;
              for (let i = 0; i < split.count; i++) {
                const a = (i / split.count) * Math.PI * 2 + Math.random() * 0.4;
                const off = h.radius * 0.6;
                createPredator(
                  world,
                  this.spawnParent,
                  childDef,
                  hp.x + Math.cos(a) * off,
                  hp.y + Math.sin(a) * off,
                  { radius: radMul, hp: radMul, damage: radMul },
                );
              }
              bus.emit('fx:particles', { x: hp.x, y: hp.y, count: 18, color: 0xff7070, spread: 80 });
            }
          }
          const r = world.get(e, Renderable);
          if (r) r.root.parent?.removeChild(r.root);
          world.destroyEntity(e);
          continue;
        }
      }
    }
    void playerEntity;
  }

  private detonateBomb(world: World, x: number, y: number): void {
    // Kill a random 70% of on-screen predators. No biomass awarded (they
    // evaporate) and the combo resets — bombs are a pressure-release item,
    // not a scoring tool.
    const all: number[] = [];
    for (const e of world.query([Hostile])) all.push(e);
    // Fisher–Yates shuffle so the 70% isn't always the first-inserted set.
    for (let i = all.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [all[i], all[j]] = [all[j], all[i]];
    }
    const killCount = Math.floor(all.length * BOMB_KILL_RATIO);
    for (let i = 0; i < killCount; i++) {
      const e = all[i];
      const hp = world.get(e, Position);
      const h = world.get(e, Hostile);
      if (hp && h) {
        bus.emit('fx:particles', { x: hp.x, y: hp.y, count: 14, color: 0xff9f6f, spread: 60 });
      }
      const r = world.get(e, Renderable);
      if (r) r.root.parent?.removeChild(r.root);
      world.destroyEntity(e);
    }
    bus.emit('powerup:bombDetonated', { x, y, killed: killCount });
    bus.emit('fx:flash', { color: 0xff7a3a, alpha: 0.5, duration: 0.35 });
    bus.emit('fx:screenShake', { intensity: 22, duration: 0.55 });
    bus.emit('fx:particles', { x, y, count: 40, color: 0xffb07a, spread: 200 });
    bus.emit('fx:floatingText', { x, y: y - 30, text: `BOOM — ${killCount} cleared`, color: 0xffb07a });
    bus.emit('audio:cue', { id: 'bomb' });
    this.combo.reset();
    if (this.streakCount > 0) {
      this.streakFoodId = null;
      this.streakCount = 0;
      bus.emit('combo:streak', { foodId: null, count: 0, multiplier: 1 });
    }
  }

  private activateShield(playerCell: any, x: number, y: number): void {
    if (playerCell) {
      playerCell.invulnRemaining = Math.max(playerCell.invulnRemaining ?? 0, SHIELD_DURATION);
    }
    bus.emit('powerup:shieldActivated', { duration: SHIELD_DURATION });
    bus.emit('fx:flash', { color: 0x4fd1c5, alpha: 0.25, duration: 0.3 });
    bus.emit('fx:particles', { x, y, count: 18, color: 0x4fd1c5, spread: 80 });
    bus.emit('fx:floatingText', { x, y: y - 30, text: 'Shielded — 5s', color: 0x4fd1c5 });
    bus.emit('audio:cue', { id: 'shield' });
  }

  private awardFood(fa: import('@/components').AbsorbableC, x: number, y: number): void {
    const effects = this.mutations.getEffects();
    const env = this.environment.getEffects();

    let biomass = fa.biomass;
    // type-specific bonuses
    if (fa.foodId === 'bacteria') {
      biomass *= 1 + (effects.biomassFromBacteria ?? 0);
      if (env.bacteriaBiomassMult) biomass *= env.bacteriaBiomassMult;
    }
    if (fa.foodId === 'sugar') {
      biomass *= 1 + (effects.biomassFromSugars ?? 0);
    }
    // flat biomass mult from upgrades/mutations
    biomass *= player.stats.biomassMult;
    // combo multiplier from ComboSystem (applied via hit event)
    this.combo.registerAbsorption();
    biomass *= this.combo.multiplier;

    // Same-type streak: only advances while the absorption halo is OFF.
    // Eating two of the same food type in a row builds a streak; eating a
    // different type, a powerup, or a predator (handled elsewhere) resets
    // it. The bonus multiplier stacks on top of the bloom-clear combo so
    // skilled play that holds both at once is rewarded.
    let streakMult = 1;
    if (this.streakActive) {
      if (this.streakFoodId === fa.foodId) {
        this.streakCount += 1;
      } else {
        this.streakFoodId = fa.foodId;
        this.streakCount = 1;
      }
      streakMult = this.streakMultiplier();
      biomass *= streakMult;
      bus.emit('combo:streak', { foodId: fa.foodId, count: this.streakCount, multiplier: streakMult });
      // Every multiple of 10 in a chain drops a stage-spawned reward
      // (currently a DNA strand). The stage decides where it lands.
      if (this.streakCount > 0 && this.streakCount % 10 === 0) {
        bus.emit('combo:streakReward', { count: this.streakCount, foodId: fa.foodId });
      }
    } else if (this.streakCount > 0) {
      // Halo got turned on — drop the streak so re-engaging is a fresh run.
      this.streakFoodId = null;
      this.streakCount = 0;
      bus.emit('combo:streak', { foodId: null, count: 0, multiplier: 1 });
    }

    biomass = Math.max(0, biomass);

    let dna = fa.dna;
    // combo milestone DNA bonus: handled in ComboSystem via event

    if (biomass > 0) player.addBiomass(biomass);
    if (dna > 0) player.addDna(dna);

    bus.emit('food:absorbed', { foodId: fa.foodId, value: biomass, dna, x, y, bloomId: fa.bloomId });
    // The audio pop fires for every absorption — it's cheap, it's the
    // tactile feedback the player relies on while sweeping food, and
    // gating it made eating proteins/sugars feel dead. Floating text is
    // the real cost (DOM-ish text spam under high combos) so that stays
    // gated to "interesting" absorptions only.
    const isShowy = dna > 0 || biomass >= 5;
    bus.emit('audio:pop', { pitch: Math.min(1.6, 0.2 + this.combo.combo * 0.02) });
    if (isShowy) {
      bus.emit('fx:floatingText', {
        x, y,
        text: (dna > 0 ? `+${dna} DNA` : `+${Math.round(biomass)}`),
        color: fa.foodId === 'dna' ? 0xB794F4 : colorForFood(fa.foodId),
      });
    }
    bus.emit('fx:particles', {
      x, y, count: isShowy ? 3 + Math.floor(Math.random() * 4) : 1 + Math.floor(Math.random() * 2),
      color: colorForFood(fa.foodId),
    });
    if (fa.codexId) bus.emit('codex:unlocked', { entryId: fa.codexId });
  }
}

function colorForFood(id: string): number {
  switch (id) {
    case 'protein': return 0xED64A6;
    case 'sugar':   return 0xF6AD55;
    case 'bacteria':return 0x68D391;
    case 'dna':     return 0xB794F4;
    default:        return 0xFFFFFF;
  }
}
